import { useState } from 'react'
import { Modal, message } from 'antd'
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons'
import QRCard from './QRCard'
import { Button } from '@/components/ui'
import { getQrCodeUrl } from '@/lib/qrCode'

/** Size (px) of the downloaded QR PNG. Larger than the 200px preview so the saved file stays crisp. */
const DOWNLOAD_QR_SIZE = 1024

interface QRPreviewModalProps {
  open: boolean
  onClose: () => void
  title: string
  url: string
  /** Data encoded in the QR image. Defaults to `url` when the QR should point at a link. */
  value?: string
  subtitle?: string
  downloadFilename?: string
}

export default function QRPreviewModal({
  open,
  onClose,
  title,
  url,
  value,
  subtitle,
  downloadFilename = 'qr-code',
}: QRPreviewModalProps) {
  const [downloading, setDownloading] = useState(false)
  const qrData = value ?? url

  const handleDownload = async () => {
    if (!qrData) return

    setDownloading(true)
    try {
      // Download only the raw QR image (no card chrome). Fetch the PNG as a blob
      // to dodge cross-origin download issues; api.qrserver.com allows CORS.
      const qrImageUrl = getQrCodeUrl(qrData, { size: DOWNLOAD_QR_SIZE })
      const response = await fetch(qrImageUrl)
      if (!response.ok) {
        throw new Error(`QR image request failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)

      const filename = downloadFilename.toLowerCase().endsWith('.png')
        ? downloadFilename
        : `${downloadFilename}.png`

      const link = document.createElement('a')
      link.download = filename
      link.href = objectUrl
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Defer revocation so the browser can finish reading the blob for the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)

      message.success('QR code downloaded')
    } catch (error) {
      console.error('Failed to download QR code:', error)
      message.error('Failed to download QR code')
    } finally {
      setDownloading(false)
    }
  }

  const handleCopyLink = async () => {
    if (!url) return

    try {
      await navigator.clipboard.writeText(url)
      message.success('QR link copied!')
    } catch (error) {
      console.error('Failed to copy QR link:', error)
      message.error('Failed to copy QR link')
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={400}
      centered
      title={null}
      closable
    >
      <div className="flex flex-col items-center py-4">
        <QRCard title={title} url={qrData} subtitle={subtitle} />

        <div className="mt-6 flex items-center gap-2">
          <Button
            variant="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
          >
            Download as PNG
          </Button>

          {url && (
            <Button
              variant="secondary"
              icon={<CopyOutlined />}
              onClick={handleCopyLink}
            >
              Copy link
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
